<template>
    <model-form
        :title="
            dataCollectionTask && dataCollectionTask.id
                ? `Edit Task for ${getTaskDisplayName}`
                : 'Create Data Collection Task'
        "
        :formFields="formFields"
        @save="save"
        @cancel="cancel"
    >
        <template v-slot:dayFieldDescription>
            <v-col>
                {{ taskDayDescription }}
            </v-col>
        </template>
        <template v-slot:dateFieldDescription>
            <v-col>&nbsp;</v-col>
        </template>
        <template v-slot:inheritedTaskGroupDescription>
            <v-col>{{ taskDayDescription }}</v-col>
        </template>
    </model-form>
</template>
<script>
import { defineComponent } from "vue";

import ModelForm from "../common/models/ModelForm.vue";
import { buildNumberField, buildSelectField, buildSlotField } from "@/lib/vueModelUtils";
import { buildDataCollectionTasksAPIForExperiment } from "@/services/api.services";
import { transformEmptyStringToNull } from "@/lib/utils";
import {
    buildDayByDateForExperimentStartDateField,
    getDateTimeForISODateOnlyString,
} from "@/lib/dateUtils";
import {
    buildReadoutGroupOptions,
    buildSelectRelativeToDay0Field,
    findTaskDayInTaskGroups,
    findTaskGroupById,
    getDefaultAnchorDateValue,
    getDefaultAnchorDateValueForTask,
    getTaskDayDescription,
    getTaskDisplayName,
} from "@/lib/taskUtils";
import _ from "lodash";
import { withLoadingScreen } from "@/lib/loading";

export default defineComponent({
    name: "data-collection-task-form",

    props: {
        dataCollectionTask: {
            type: Object,
            required: false,
        },
        readoutGroups: {
            type: Array,
            required: true,
        },
        taskGroups: {
            type: Array,
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
    },

    components: {
        ModelForm,
    },

    data() {
        return {
            taskDayDescription: "",
            taskGroupField: null,
            readoutGroupField: null,
            typeField: null,
            overrideTaskGroupDayField: null,
            specifyRelativeToDay0Field: null,
            dayField: null,
            relativeDateField: null,
            dateField: null,
            internalField: null,
            inheritedTaskGroupDescriptionField: null,
            formFields: [],
        };
    },

    async mounted() {
        this.dataCollectionTasksAPI = buildDataCollectionTasksAPIForExperiment(
            this.experiment.code
        );
        this.buildFormFields();
    },

    methods: {
        getDay() {
            if (!this.dataCollectionTask.id || this.isAdHocTask) {
                return null;
            }
            return findTaskDayInTaskGroups(
                this.dataCollectionTask,
                this.taskGroups,
                this.experiment.day0
            );
        },
        buildFormFields: function () {
            this.taskGroupField = this.buildTaskGroupFormField();
            this.readoutGroupField = this.buildReadoutGroupFormField();
            this.overrideTaskGroupDayField = this.buildOverrideTaskGroupDayFormField();
            this.specifyRelativeToDay0Field = this.buildSpecifyRelativeToDay0FormField();
            this.dayField = this.buildSpecifyDayFormField();
            this.relativeDateField = this.buildRelativeDateFormField();
            this.dateField = this.buildDateFormField();
            this.internalField = this.buildInternalField();

            this.inheritedTaskGroupDescriptionField = buildSlotField({
                slot: "inheritedTaskGroupDescription",
                cols: 12,
            });

            this.typeField = buildSelectField({
                name: "Type",
                options: [
                    { title: "Scheduled", value: "scheduled" },
                    { title: "Ad-Hoc", value: "ad-hoc" },
                ],
                value: this.dataCollectionTask.type,
            });

            this.updateFormFields();
            this.updateTaskDayDescription();
        },
        buildTaskGroupFormField() {
            const taskGroupOptions = this.taskGroups.map((taskGroup) => {
                return {
                    title: taskGroup.displayName,
                    value: taskGroup.id,
                };
            });

            const taskGroupId = this.dataCollectionTask.taskGroupId;

            return buildSelectField({
                name: "Task Group",
                options: taskGroupOptions,
                value: taskGroupId,
                changed: (taskGroupId) => {
                    const taskGroup = findTaskGroupById(taskGroupId, this.taskGroups);
                    this.updateTaskGroupSelection(taskGroup);
                },
            });
        },
        buildReadoutGroupFormField() {
            const readoutGroupOptions = buildReadoutGroupOptions(this.readoutGroups);

            return buildSelectField({
                name: "Readout Group",
                options: readoutGroupOptions,
                value: this.dataCollectionTask.readoutGroupId,
            });
        },
        buildOverrideTaskGroupDayFormField() {
            const hasOverriddenValue =
                !_.isNil(this.dataCollectionTask.date) || !_.isNil(this.dataCollectionTask.day);

            return {
                fieldType: "boolean",
                fieldDisplayName: "Override Task Group Day",
                value: hasOverriddenValue,
                disabled: _.isNil(this.taskGroupField.value),
                changed: () => {
                    this.toggleOverrideTaskGroup();
                },
            };
        },
        buildSpecifyRelativeToDay0FormField() {
            return buildSelectRelativeToDay0Field({
                value: false,
                disabled: !this.overrideTaskGroupDayField.value,
                changed: () => {
                    this.updateFormFields();
                    this.updateTaskDayDescription();
                },
            });
        },
        buildSpecifyDayFormField() {
            const taskDay = this.dataCollectionTask.day;
            const dayField = buildNumberField({ label: "Day", value: taskDay });
            dayField.value = this.getDay();
            dayField.disabled = !this.overrideTaskGroupDayField.value;
            dayField.changed = () => {
                this.updateDateFieldFromDayField();
                this.updateTaskDayDescription();
            };

            dayField.appendSlot = "dayFieldDescription";

            return dayField;
        },
        buildRelativeDateFormField() {
            const day = this.isAdHocTask ? 0 : this.getDay();
            const relativeDateField = buildDayByDateForExperimentStartDateField(
                day,
                this.experiment.day0,
                (value) => {
                    this.dayField.value = value;
                }
            );
            relativeDateField.disabled = this.isAdHocTask || !this.overrideTaskGroupDayField.value;
            relativeDateField.appendSlot = "dateFieldDescription";

            return relativeDateField;
        },
        buildDateFormField() {
            return {
                fieldType: "date",
                fieldDisplayName: "Date",
                cols: 12,
                disabled: !this.overrideTaskGroupDayField.value,
                value: this.getDefaultAnchorDateValue(),
            };
        },
        buildInternalField() {
            return {
                fieldType: "boolean",
                fieldDisplayName: "Internal",
                cols: 2,
                disabled: false,
                value: this.dataCollectionTask.internal,
            };
        },
        updateFormFields() {
            const formFields = [
                this.readoutGroupField,
                this.taskGroupField,
                this.typeField,
                this.overrideTaskGroupDayField,
                this.internalField,
            ];

            if (this.overrideTaskGroupDayField.value) {
                // If the user is overriding the task group day, show the day and date fields
                formFields.push(this.specifyRelativeToDay0Field);

                if (this.specifyRelativeToDay0Field.value) {
                    // If the user is specifying the day relative to day 0, show the relative date fields
                    formFields.push(this.dayField, this.relativeDateField);
                } else {
                    // otherwise, show the "anchor" date field
                    formFields.push(this.dateField);
                }
            } else {
                // If the user is not overriding the task group day, show the inherited description
                formFields.push(this.inheritedTaskGroupDescriptionField);
            }

            this.formFields = formFields;
        },
        toggleOverrideTaskGroup() {
            const overrideTaskGroup = this.overrideTaskGroupDayField.value;
            // set the override fields to disabled if not overriding, otherwise enable them.
            this.dayField.disabled = !overrideTaskGroup;
            this.relativeDateField.disabled = !overrideTaskGroup;
            this.dateField.disabled = !overrideTaskGroup;
            this.specifyRelativeToDay0Field.disabled = !overrideTaskGroup;
            this.updateFormFields();
            this.updateTaskDayDescription();
        },
        updateTaskGroupSelection(taskGroup) {
            this.overrideTaskGroupDayField.disabled = false;
            this.specifyRelativeToDay0Field.value = _.isNil(taskGroup.date);

            if (!this.overrideTaskGroupDayField.value) {
                this.dayField.value = taskGroup.day || 0;
                this.dateField.value = getDefaultAnchorDateValue(taskGroup.date);
                this.updateDateFieldFromDayField();
            }

            this.updateFormFields();
            this.updateTaskDayDescription();
        },
        updateDateFieldFromDayField() {
            const day = this.dayField.value;
            const date =
                day === "" ? this.experiment.day0 : this.experiment.day0.plus({ days: day });
            this.relativeDateField.value = date.toISODate();
        },
        updateTaskDayDescription() {
            this.taskDayDescription = getTaskDayDescription({
                day: this.dayField.value,
                date: this.dateField.value,
                isRelativeDay0: this.specifyRelativeToDay0Field.value,
                isInheritedFromTaskGroup: !this.overrideTaskGroupDayField.value,
                selectedTaskGroupId: this.taskGroupField.value,
                taskGroups: this.taskGroups,
            });
        },
        getDefaultAnchorDateValue() {
            return getDefaultAnchorDateValueForTask({
                dataCollectionTask: this.dataCollectionTask,
                taskGroups: this.taskGroups,
                overrideTaskGroupDay: this.overrideTaskGroupDayField.value,
                experimentStartDate: this.experiment.day0,
            });
        },
        async save() {
            await withLoadingScreen(async () => {
                const dataCollectionTask = {
                    ...this.dataCollectionTask,
                };

                if (this.overrideTaskGroupDayField.value) {
                    if (this.specifyRelativeToDay0Field.value) {
                        dataCollectionTask.day = transformEmptyStringToNull(this.dayField.value);
                        dataCollectionTask.date = null;
                    } else {
                        dataCollectionTask.day = null;
                        dataCollectionTask.date = getDateTimeForISODateOnlyString(
                            this.dateField.value
                        );
                    }
                } else {
                    dataCollectionTask.day = null;
                    dataCollectionTask.date = null;
                }
                dataCollectionTask.type = this.typeField.value;
                dataCollectionTask.taskGroupId = this.taskGroupField.value;
                dataCollectionTask.readoutGroupId = this.readoutGroupField.value;
                dataCollectionTask.internal = this.internalField.value;
                if (this.dataCollectionTask.id) {
                    await this.dataCollectionTasksAPI.update(dataCollectionTask);
                } else {
                    await this.dataCollectionTasksAPI.create(dataCollectionTask);
                }
            });
            this.$emit("saved");
        },
        cancel() {
            this.$emit("closed");
        },
        close() {
            this.$emit("closed");
        },
    },

    computed: {
        isAdHocTask() {
            return this.dataCollectionTask.type === "ad-hoc";
        },
        getTaskDisplayName() {
            const taskGroup = findTaskGroupById(
                this.dataCollectionTask.taskGroupId,
                this.taskGroups
            );
            if (!taskGroup) {
                return "";
            }
            return getTaskDisplayName(
                this.dataCollectionTask,
                taskGroup,
                null,
                this.experiment.day0
            );
        },
    },
});
</script>
